import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { TenantService } from '../tenant/tenant.service';

/**
 * Sends an agency admin landing on `/app` to their Portfolio instead of an individual site's
 * dashboard — an agency logs in to the portfolio, never to the single-site view. Non-agency callers
 * pass through to `firstSiteRedirectGuard`. A failed tenant read passes through rather than trapping
 * the user, so a transient error never blocks login.
 */
export const agencyHomeRedirectGuard: CanActivateFn = () => {
  const tenant = inject(TenantService);
  const router = inject(Router);

  return tenant.fetchMe().pipe(
    map((info) => (info.isAgencyAdmin ? router.createUrlTree(['/app/agency/portfolio']) : true)),
    catchError(() => of(true)),
  );
};

/**
 * Keeps an agency admin inside the agency shell: `/app/sites/:siteId/...` is the individual view, so
 * it is rewritten to the same page under `/app/agency/sites/:siteId/...`. Fails open like the guard above.
 */
export const agencySiteRedirectGuard: CanActivateFn = (_route, state) => {
  const tenant = inject(TenantService);
  const router = inject(Router);

  return tenant.fetchMe().pipe(
    map((info) =>
      info.isAgencyAdmin ? router.parseUrl(state.url.replace('/app/sites/', '/app/agency/sites/')) : true,
    ),
    catchError(() => of(true)),
  );
};
